import { useCallback, useRef, useEffect } from "react";
import type { ChatMessage, UsageInfoResponse } from "@/domain/aiAgent";
import { ApiClientError } from "@/application/errors";
import { useAiAgentRepository } from "../useRepositories";
import { useAiAgentFeedback } from "./useAiAgentFeedback";

interface UseAiAgentChatMessagesOptions {
  sessionId: string;
  quotaExceeded: boolean;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setUsageInfo: React.Dispatch<React.SetStateAction<UsageInfoResponse | null>>;
  setQuotaExceeded: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useAiAgentChatMessages = ({
  sessionId,
  quotaExceeded,
  setMessages,
  setIsLoading,
  setUsageInfo,
  setQuotaExceeded,
}: UseAiAgentChatMessagesOptions) => {
  const aiAgentRepository = useAiAgentRepository();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const { submitFeedback } = useAiAgentFeedback({
    aiAgentRepository,
    sessionId,
    setMessages,
  });

  const sendQuestion = useCallback(
    async (question: string) => {
      if (quotaExceeded) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: question.trim(),
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await aiAgentRepository.sendQuery(
          question.trim(),
          sessionId,
          controller.signal,
        );

        if (controller.signal.aborted) return;

        if (response.usage) {
          setUsageInfo(response.usage);
          if (response.usage.pct_used >= 100) setQuotaExceeded(true);
        }

        let content: string;
        let role: ChatMessage["role"] = "assistant";

        if (response.status === "completed") {
          content = response.answer;
        } else if (response.status === "rejected") {
          content = response.answer || "agent.status_rejected";
        } else if (response.status === "clarification_needed") {
          content = response.answer || "agent.status_clarification";
        } else {
          content = response.error_message || "agent.error_generic";
          role = "error";
        }

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role,
          content,
          timestamp: Date.now(),
          toolUsed: response.tool_used ?? undefined,
          sources: response.sources ?? undefined,
          queryId: response.query_id,
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        if (controller.signal.aborted) return;

        let errorKey = "agent.error_generic";
        if (error instanceof ApiClientError) {
          if (error.code === "TIMEOUT") errorKey = "agent.error_timeout";
          else if (error.code === "NETWORK_ERROR") errorKey = "agent.error_network";
          else if (error.status === 403) errorKey = "agent.module_not_enabled";
          else if (error.status === 429) {
            errorKey = "agent.usage_exceeded";
            setQuotaExceeded(true);
          }
        }

        if (error instanceof DOMException && error.name === "AbortError") return;

        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "error",
            content: errorKey,
            timestamp: Date.now(),
          },
        ]);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    },
    [
      aiAgentRepository,
      sessionId,
      quotaExceeded,
      setMessages,
      setIsLoading,
      setUsageInfo,
      setQuotaExceeded,
    ],
  );

  return { sendQuestion, submitFeedback, abortControllerRef };
};
